// One read-only model for the exercise list, detail view and charts.
// Missing measurements stay missing; they must never become zero-valued drops.
export function measurement(value) {
  if (value === null || value === undefined || (typeof value === "string" && !value.trim()) || !["number", "string"].includes(typeof value)) return null
  const number = Number(typeof value === "string" ? value.trim().replace(",", ".") : value)
  return Number.isFinite(number) && number >= 0 ? number : null
}

export function durationSeconds(value) {
  if (typeof value !== "string" || !value.includes(":")) return measurement(value)
  const parts = value.trim().replace(",", ".").split(":")
  if (parts.length < 2 || parts.length > 3 || parts.some(part => !/^\d+(\.\d+)?$/.test(part))) return null
  const numbers = parts.map(Number)
  if (numbers.slice(1).some(part => part >= 60)) return null
  return numbers.reduce((total, part) => total * 60 + part, 0)
}

const zurichDay = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Zurich", year: "numeric", month: "2-digit", day: "2-digit" })
export function dateValue(value) {
  if (value === null || value === undefined || value === "" || typeof value === "boolean") return null
  const calendar = typeof value === "string" && /^(\d{4})-(\d{2})-(\d{2})(?:T|$)/.exec(value)
  if (calendar) {
    const check = new Date(Date.UTC(Number(calendar[1]), Number(calendar[2]) - 1, Number(calendar[3])))
    if (check.getUTCFullYear() !== Number(calendar[1]) || check.getUTCMonth() + 1 !== Number(calendar[2]) || check.getUTCDate() !== Number(calendar[3])) return null
  }
  let timestamp
  if (typeof value === "number" || /^\d+(\.\d+)?$/.test(String(value))) {
    const numeric = Number(value)
    timestamp = numeric < 1e12 ? numeric * 1000 : numeric
  } else timestamp = new Date(value).getTime()
  if (!Number.isFinite(timestamp) || timestamp <= 0 || !Number.isFinite(new Date(timestamp).getTime())) return null
  const parts = zurichDay.formatToParts(timestamp)
  const part = type => parts.find(item => item.type === type).value
  const day = `${part("year")}-${part("month")}-${part("day")}`
  return { timestamp, day, x: Date.parse(day + "T12:00:00Z") }
}

export const METRICS = {
  weight: { label: "Gewicht", title: "Höchstes Gewicht", unit: "kg", rule: "Höchstes Gewicht pro Trainingstag" },
  reps: { label: "Wiederholungen", title: "Meiste Wiederholungen", unit: "Wdh.", rule: "Meiste Wiederholungen in einem Satz pro Trainingstag" },
  volume: { label: "Volumen", title: "Trainingsvolumen", unit: "kg", rule: "Gewicht × Wiederholungen, summiert pro Trainingstag" },
  e1rm: { label: "1RM", title: "Geschätztes 1RM", unit: "kg", rule: "Schätzung nach Epley aus Sätzen mit 1–20 Wiederholungen" },
  duration: { label: "Dauer", title: "Längste Dauer", unit: "", rule: "Längste aufgezeichnete Satzdauer pro Trainingstag" },
  distance: { label: "Distanz", title: "Weiteste Distanz", unit: "m", rule: "Weiteste aufgezeichnete Distanz pro Trainingstag" },
}

export function formatNumber(value) {
  return Number.isFinite(value) ? new Intl.NumberFormat("de-CH", { maximumFractionDigits: 1 }).format(value) : "–"
}
export function formatDuration(value) {
  if (!Number.isFinite(value)) return "–"
  const seconds = Math.round(value), hours = Math.floor(seconds / 3600), minutes = Math.floor(seconds % 3600 / 60), rest = seconds % 60
  return hours ? `${hours} h ${String(minutes).padStart(2, "0")}` : minutes ? `${minutes}:${String(rest).padStart(2, "0")} min` : `${rest} s`
}
export function metricValue(value, metric, withUnit = true) {
  if (metric === "duration") return formatDuration(value)
  return formatNumber(value) + (withUnit && Number.isFinite(value) ? " " + METRICS[metric].unit : "")
}
export function dayLabel(day, short = false) {
  if (!day) return "Datum unbekannt"
  return new Intl.DateTimeFormat("de-CH", { timeZone: "UTC", day: "2-digit", month: short ? "2-digit" : "short", ...(short ? {} : { year: "numeric" }) }).format(new Date(day + "T12:00:00Z"))
}
export function exerciseImage(exercise) {
  const picture = exercise?.picture || exercise?.manual?.[0]?.picture
  return typeof picture === "string" ? `/img/uebungen/${picture.replace(/\.[^/.]+$/, "")}.webp` : ""
}
export function exerciseTags(exercise) {
  const result = new Map()
  for (const tag of Array.isArray(exercise?.tags) ? exercise.tags : []) {
    if (!tag || typeof tag.name !== "string" || !tag.name.trim()) continue
    const key = String(tag.class || tag._id || tag.name)
    result.set(key, { ...tag, key, name: tag.name.trim() })
  }
  return [...result.values()]
}

export function buildExerciseStatistics(entry = {}) {
  const sessions = new Map(), seen = new Set(), records = {}, totals = { reps: 0, volume: 0, duration: 0, distance: 0, sets: 0 }
  let unknownDates = 0
  for (const [index, raw] of (Array.isArray(entry.sets) ? entry.sets : []).entries()) {
    if (!raw || typeof raw !== "object") continue
    if (raw._id && seen.has(String(raw._id))) continue
    if (raw._id) seen.add(String(raw._id))
    const date = dateValue(raw.date) || dateValue(raw.timestamp)
    const steps = (Array.isArray(raw.steps) ? raw.steps : []).filter(step => step && typeof step === "object").map((step, position) => {
      const weight = measurement(step.weight), reps = measurement(step.reps)
      const duration = durationSeconds(step.time) ?? measurement(step.duration)
      const distance = measurement(step.distance)
      const volume = weight !== null && reps !== null ? weight * reps : null
      const e1rm = weight > 0 && reps >= 1 && reps <= 20 ? (reps === 1 ? weight : weight * (1 + reps / 30)) : null
      return { weight, reps, volume, duration, distance, e1rm, position: position + 1, note: step.note || "", warmup: step.isWarmup === true }
    }).filter(step => [step.weight, step.reps, step.duration, step.distance].some(value => value !== null))
    if (!steps.length) continue
    if (!date) unknownDates++
    const key = raw.clientSessionId ? `session:${raw.clientSessionId}` : date ? `${date.timestamp}:${raw.plan_id || ""}:${raw.split || ""}` : `unknown:${raw._id || index}`
    if (!sessions.has(key)) sessions.set(key, { key, day: date?.day || null, timestamp: date?.timestamp || 0, title: String(raw.split || "Training"), steps: [] })
    const session = sessions.get(key)
    session.steps.push(...steps)
    // Out-of-order documents belonging to one session use its earliest timestamp.
    if (date && (!session.timestamp || date.timestamp < session.timestamp)) { session.timestamp = date.timestamp; session.day = date.day }
    for (const step of steps) {
      totals.sets++
      for (const key of ["reps", "volume", "duration", "distance"]) totals[key] += step[key] || 0
      for (const metric of Object.keys(METRICS)) {
        const value = step[metric]
        if (value > 0 && (!records[metric] || value > records[metric].value)) records[metric] = { value, day: date?.day || null, step }
      }
    }
  }
  const history = [...sessions.values()].sort((a, b) => b.timestamp - a.timestamp || a.key.localeCompare(b.key))
  const days = new Map()
  for (const session of history) {
    if (!session.day) continue
    if (!days.has(session.day)) days.set(session.day, { day: session.day, x: Date.parse(session.day + "T12:00:00Z"), sessions: 0, values: {} })
    const day = days.get(session.day)
    day.sessions++
    for (const step of session.steps) for (const key of Object.keys(METRICS)) {
      if (step[key] === null) continue
      day.values[key] = key === "volume" ? (day.values[key] || 0) + step[key] : Math.max(day.values[key] ?? -Infinity, step[key])
    }
  }
  const timeline = [...days.values()].sort((a, b) => a.x - b.x)
  const metrics = Object.keys(METRICS).filter(key => Boolean(records[key]))
  const primary = records.weight ? "weight" : records.distance ? "distance" : records.duration ? "duration" : "reps"
  return { history, timeline, records, totals, metrics, primary, unknownDates, tags: exerciseTags(entry.exercise), latestDay: timeline[timeline.length - 1]?.day || null }
}

export function exerciseSeries(stats, metric, range = "all", now = Date.now()) {
  const cutoff = range === "all" ? -Infinity : Date.parse((dateValue(now)?.day || "1970-01-01") + "T00:00:00Z") - (Number(range) - 1) * 86400000
  return stats.timeline.filter(day => day.x >= cutoff && Number.isFinite(day.values[metric])).map(day => ({ x: day.x, y: day.values[metric], day: day.day, sessions: day.sessions }))
}

export function chartGeometry(series, width = 600, height = 190) {
  // Numeric chronological sorting, one point per x, straight segments: no spline overshoot.
  const unique = new Map()
  for (const point of series || []) if (Number.isFinite(point.x) && Number.isFinite(point.y)) unique.set(point.x, point)
  const data = [...unique.values()].sort((a, b) => a.x - b.x)
  if (!data.length) return { points: [], line: "", area: "", ticks: [] }
  const low = Math.min(...data.map(point => point.y)), high = Math.max(...data.map(point => point.y))
  const padding = Math.max((high - low) * .2, high * .04, 1)
  const min = Math.max(0, low - padding), max = high + padding
  const first = data[0].x, last = data[data.length - 1].x
  const points = data.map(point => ({ ...point, px: last === first ? width / 2 : 14 + (point.x - first) / (last - first) * (width - 28), py: 12 + (1 - (point.y - min) / (max - min)) * (height - 24) }))
  const line = points.map((point, index) => `${index ? "L" : "M"}${point.px.toFixed(2)},${point.py.toFixed(2)}`).join(" ")
  return { points, line, area: `${line} L${points[points.length - 1].px},${height} L${points[0].px},${height} Z`, ticks: [max, (max + min) / 2, min] }
}

export function filterExercises(items, { search = "", tags = [], type = "all", sort = "recent" } = {}) {
  const normalise = value => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
  const terms = normalise(search).trim().split(/\s+/).filter(Boolean)
  const result = items.filter(item => {
    const text = normalise([item.entry.exercise.title, ...item.stats.tags.map(tag => tag.name)].join(" "))
    return terms.every(term => text.includes(term)) && (!tags.length || item.stats.tags.some(tag => tags.includes(tag.key))) &&
      (type === "all" || (type === "strength" ? item.stats.records.weight || item.stats.records.reps : type === "duration" ? item.stats.records.duration : item.stats.records.distance))
  })
  return result.sort((a, b) => {
    if (sort === "name") return a.entry.exercise.title.localeCompare(b.entry.exercise.title, "de")
    const value = item => sort === "frequent" ? item.stats.history.length : sort === "weight" ? item.stats.records.weight?.value || 0 : sort === "duration" ? item.stats.records.duration?.value || 0 : sort === "distance" ? item.stats.records.distance?.value || 0 : item.stats.history[0]?.timestamp || 0
    return value(b) - value(a) || a.entry.exercise.title.localeCompare(b.entry.exercise.title, "de")
  })
}
